import { BasketApiService } from '@api_patient/basket_api.service';
import { DynamicFormsApiService } from '@api_patient/dynamic_forms_api.service';
import { AnalyticService } from '@application/analytic/analytic.service';
import { UniversalConfig } from '@application/universal_config.service';
import { NotificationService } from '@component/notification_manager/notification/mod';
import { UnhandledIssuesService } from '@feature/issues/unhandled_issues.service';
import type { FormOpportunity } from '@visiba/api-vcs-client-v2';
import { Injectable, Scopes } from '@visiba/instantiation';
import { FormDataProvider } from './form_data_providers/form_data_provider';
import { FormDataProviderBasket } from './form_data_providers/form_data_provider_basket';
import { FormDataProviderLocal } from './form_data_providers/form_data_provider_local';
import { FormDataProviderRespondant } from './form_data_providers/form_data_provider_respondant';
import { FormWorker } from './form_worker';

interface FormDataProviderRespondantType {
	type: 'respondant';
	respondantId: number;
}

interface FormDataProviderBasketType {
	type: 'basket';
	basketId: number;
	basketProductId: number;
}

interface FormDataProviderLocalType {
	type: 'local';
	id: Guid;
}

// We need a common interface between API and sockets results. The API uses
// PascalCase serialization while the socket uses lowerCase.
export interface ExoticRespondantStatusApiModel {
	respondantId?: number;
	basketId?: number;
	basketProductId?: number;
	formId: number;
	header: string;
	opportunity: FormOpportunity;
	completed: boolean;
	issueTrackingId: string;
	editable?: boolean;
	dataProvider: FormDataProviderRespondantType | FormDataProviderBasketType | FormDataProviderLocalType;
}

interface Filter {
	completed?: boolean;
	opportunity?: FormOpportunity | FormOpportunity[];
}

@Injectable({
	scope: Scopes.Transient,
})
export class FormManager {
	private readonly associatedUnitId: number;
	private forms: FormWorker[] = [];

	constructor(
		private readonly notificationService: NotificationService,
		private readonly unhandledIssuesService: UnhandledIssuesService,
		private readonly analyticService: AnalyticService,
		private readonly universalConfig: UniversalConfig,
		private readonly formsApiService: DynamicFormsApiService,
		private readonly basketApiService: BasketApiService,
	) {
		this.associatedUnitId = this.universalConfig.selectedUnitId;
	}

	public get length(): number {
		return this.forms.length;
	}

	public countForms(filter: Filter): number {
		return this.filterForms(filter).length;
	}

	public addRespondants(respondantStatusesCluster: ExoticRespondantStatusApiModel[]): Readonly<Readonly<FormWorker>[]> {
		const refArr: FormWorker[] = [];

		for (const respondant of respondantStatusesCluster) {
			const instanceKey = this.getInstanceKey(respondant);
			const existingForm = this.forms.find((form) => form.instanceKey === instanceKey);
			if (existingForm != null) {
				existingForm.updateStatus({
					completed: respondant.completed,
					editable: respondant.editable ?? false,
					header: respondant.header,
				});
				continue;
			}

			refArr.push(
				new FormWorker(
					{
						instanceKey,
						completed: respondant.completed,
						editable: respondant.editable ?? false,
						formId: respondant.formId,
						header: respondant.header,
						opportunity: respondant.opportunity,
						issueTrackingId: respondant.issueTrackingId,
						dataProvider: this.createDataProvider(respondant.dataProvider, respondant.formId),
					},
					this.associatedUnitId,
					this.notificationService,
					this.unhandledIssuesService,
					this.analyticService,
				),
			);
		}

		this.forms.push(...refArr);

		return refArr;
	}

	private createDataProvider(
		dataProviderType: FormDataProviderRespondantType | FormDataProviderBasketType | FormDataProviderLocalType,
		formId: number,
	): FormDataProvider {
		if (dataProviderType.type === 'respondant') {
			return new FormDataProviderRespondant(this.associatedUnitId, formId, dataProviderType.respondantId, this.formsApiService);
		}

		if (dataProviderType.type === 'basket') {
			return new FormDataProviderBasket(
				this.associatedUnitId,
				formId,
				dataProviderType.basketId,
				dataProviderType.basketProductId,
				this.formsApiService,
				this.basketApiService,
			);
		}

		if (dataProviderType.type === 'local') {
			return new FormDataProviderLocal(this.associatedUnitId, formId, dataProviderType.id, this.formsApiService);
		}

		throw new Error('Unknown data provider type');
	}

	public hasForm(respondant: ExoticRespondantStatusApiModel): boolean {
		const instanceKey = this.getInstanceKey(respondant);
		return this.forms.some((form) => form.instanceKey === instanceKey);
	}

	private getInstanceKey(respondant: ExoticRespondantStatusApiModel): string {
		const provider = respondant.dataProvider;
		switch (provider.type) {
			case 'respondant':
				return `respondant:${provider.respondantId}`;
			case 'basket':
				return `basket:${provider.basketId}:${provider.basketProductId}:${respondant.formId}`;
			case 'local':
				return `local:${provider.id}:${respondant.formId}`;
		}
	}

	public getNextUncompletedForm(filter?: Filter): Readonly<FormWorker> | null {
		const filteredForms = filter != null ? this.filterForms(filter) : this.forms;

		return filteredForms.find((form) => !form.getState().completed) ?? null;
	}

	public getFormByFormId(formId: number): FormWorker | null {
		return this.forms.find((form) => form.formId === formId) ?? null;
	}

	public getForms(): Readonly<Readonly<FormWorker>[]> {
		return this.forms;
	}

	public filterForms(filter: Filter): Readonly<Readonly<FormWorker>[]> {
		const opportunity = Array.isArray(filter.opportunity) ? filter.opportunity : [filter.opportunity];
		const filteredForms: FormWorker[] = [];
		for (const form of this.forms) {
			const formState = form.getState();

			if (filter.completed != null && formState.completed !== filter.completed) {
				continue;
			}
			if (filter.opportunity != null && !opportunity.includes(form.opportunity)) {
				continue;
			}

			filteredForms.push(form);
		}

		return filteredForms;
	}

	public removeForms() {
		this.forms.forEach((x) => x.dispose());

		this.forms = [];
	}
}
